<template>
  <v-container>
    <v-card
      outlined
      class="pa-3 mb-3"
    >
      <v-row dense>
        <v-col
          cols="12"
          md="6"
        >
          <v-text-field
            v-model="filter"
            outlined
            dense
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            label="ຄົ້ນຫາ: ຊື່, ເບີໂທ, CUS_"
            @keyup.enter="load(1)"
            @click:clear="clearFilter"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
          class="d-flex align-center flex-wrap"
        >
          <v-btn
            color="primary"
            depressed
            class="mr-2 mb-1"
            @click="load(1)"
          >
            <v-icon left>
              mdi-magnify
            </v-icon>
            ຄົ້ນຫາ
          </v-btn>
          <v-btn
            color="success"
            depressed
            class="mr-2 mb-1"
            @click="importDialog = true"
          >
            <v-icon left>
              mdi-file-import
            </v-icon>
            Import Excel
          </v-btn>
          <v-btn
            text
            color="success"
            class="mb-1"
            :loading="downloading"
            @click="downloadTemplate"
          >
            <v-icon left>
              mdi-microsoft-excel
            </v-icon>
            ດາວໂຫລດແບບຟອມ
          </v-btn>
        </v-col>
      </v-row>
      <div class="text-caption grey--text mt-2">
        ສະແດງສະເພາະລູກຄ້າສາຍບ້ານ. ລູກຄ້າທີ່ມີບິນ "ຈ່າຍແລ້ວ ກວດສອບສຳເລັດ" ຫຼື "ຈ່າຍແລ້ວ ລໍຖ້າກວດສອບ"
        ເລືອກລຶບບໍ່ໄດ້. ຄຳຂໍຕ້ອງຜ່ານການອະນຸມັດ 3 ຂັ້ນ (ຄົນລະຄົນ) ຈຶ່ງຈະລຶບ;
        ຕອນລຶບ ການຈ່າຍ QR ທີ່ຍັງລໍຖ້າຂອງລູກຄ້າຈະຖືກຍົກເລີກ.
      </div>
    </v-card>

    <v-card
      v-if="selected.length"
      outlined
      class="pa-3 mb-3"
    >
      <div class="mb-2 font-weight-medium">
        ລູກຄ້າທີ່ເລືອກ {{ selected.length }} ລາຍ
      </div>
      <v-chip
        v-for="c in selected"
        :key="c.id"
        small
        close
        class="mr-1 mb-1"
        @click:close="unselect(c)"
      >
        {{ c.customer_code }} · {{ c.name }}
      </v-chip>
      <v-textarea
        v-model="reason"
        outlined
        dense
        rows="2"
        auto-grow
        class="mt-2"
        label="ເຫດຜົນທີ່ຂໍລຶບ *"
        :error-messages="reasonError"
        @input="reasonError = ''"
      />
      <div class="d-flex">
        <v-spacer />
        <v-btn
          color="error"
          depressed
          :loading="sending"
          @click="openConfirm"
        >
          <v-icon left>
            mdi-send
          </v-icon>
          ສົ່ງຂໍລຶບ {{ selected.length }} ລາຍ
        </v-btn>
      </div>
    </v-card>

    <v-card outlined>
      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="customers"
        :loading="loading"
        item-key="id"
        show-select
        selectable-key="selectable"
        :item-class="rowClass"
        hide-default-footer
        disable-pagination
        disable-sort
        no-data-text="ບໍ່ພົບລູກຄ້າ"
      >
        <template v-slot:[`item.name`]="{ item }">
          <div>{{ item.name }}</div>
          <div class="text-caption grey--text">
            {{ item.customer_code }} · {{ item.phone }}
          </div>
        </template>
        <template v-slot:[`item.address`]="{ item }">
          {{ [item.village, item.district].filter(Boolean).join(', ') }}
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            x-small
            :color="item.status === 'inactive' ? 'grey lighten-2' : 'green lighten-4'"
          >
            {{ item.status }}
          </v-chip>
          <div
            v-if="item.blocked_reason"
            class="text-caption red--text"
          >
            {{ item.blocked_reason }}
          </div>
        </template>
      </v-data-table>
      <v-pagination
        v-if="pagination.total_pages > 1"
        v-model="page"
        :length="pagination.total_pages"
        :total-visible="7"
        class="my-2"
        @input="load"
      />
    </v-card>

    <h3 class="mt-6 mb-2">
      ຄຳຂໍຂອງຂ້ອຍ (ລໍຖ້າ / ຖືກປະຕິເສດ)
    </h3>
    <v-card outlined>
      <v-data-table
        :headers="mineHeaders"
        :items="mine"
        :items-per-page="20"
        no-data-text="ບໍ່ມີຄຳຂໍທີ່ຄ້າງ"
      >
        <template v-slot:[`item.customer_name`]="{ item }">
          <div>{{ item.customer_name }}</div>
          <div class="text-caption grey--text">
            {{ item.customer_code }} · {{ item.customer_phone }}
          </div>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            x-small
            :color="item.status === 'rejected' ? 'red lighten-5' : 'amber lighten-5'"
            :text-color="item.status === 'rejected' ? 'red darken-2' : 'amber darken-4'"
          >
            {{ item.status === 'rejected' ? `ຖືກປະຕິເສດ (ຂັ້ນທີ ${item.step})` : `ລໍຖ້າອະນຸມັດ ຂັ້ນທີ ${item.step}/3` }}
          </v-chip>
          <div
            v-if="item.reject_reason"
            class="text-caption red--text"
          >
            {{ item.reject_reason }}
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
      v-model="confirm"
      max-width="460"
    >
      <v-card>
        <v-card-title>ຢືນຢັນສົ່ງຂໍລຶບລູກຄ້າ</v-card-title>
        <v-card-text>
          ສົ່ງຄຳຂໍລຶບລູກຄ້າສາຍບ້ານ {{ selected.length }} ລາຍ?
          ລູກຄ້າຈະຖືກລຶບ (ພ້ອມບິນ ແລະ ບັນຊີ login) ເມື່ອຜ່ານການອະນຸມັດຄົບ 3 ຂັ້ນ.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="confirm = false"
          >
            ຍົກເລີກ
          </v-btn>
          <v-btn
            color="error"
            depressed
            :loading="sending"
            @click="send"
          >
            ສົ່ງຂໍລຶບ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="importDialog"
      max-width="560"
    >
      <v-card>
        <v-card-title>Import ລູກຄ້າທີ່ຈະຂໍລຶບ (Excel)</v-card-title>
        <v-card-text>
          <div class="text-body-2 mb-3">
            ຖັນ A: ລະຫັດລູກຄ້າ (CUS_123 ຫຼື 123), ຖັນ B: ເບີໂທລູກຄ້າ, ຖັນ C: ເຫດຜົນ. ແຖວທີ 1 ເປັນຫົວຖັນ.
            <b>ຕ້ອງໃສ່ຄົບທັງ 3 ຖັນ</b> ແລະ ເບີໂທຕ້ອງກົງກັບລະຫັດລູກຄ້າ.
            ແຖວທີ່ຖືກຕ້ອງຈະຖືກສົ່ງເປັນຄຳຂໍ, ແຖວທີ່ບໍ່ຜ່ານຈະສະແດງເຫດຜົນ. <b>ສູງສຸດ 500 ແຖວຕໍ່ໄຟລ໌.</b>
            <a @click="downloadTemplate">ດາວໂຫລດແບບຟອມ</a>
          </div>
          <v-file-input
            v-model="file"
            accept=".xlsx,.xls"
            outlined
            dense
            show-size
            label="ໄຟລ໌ Excel (.xlsx)"
            :error-messages="fileError"
            @change="fileError = ''"
          />
          <template v-if="importResult">
            <v-alert
              type="success"
              text
              dense
            >
              ສົ່ງຄຳຂໍແລ້ວ {{ importResult.created }} ລາຍ
            </v-alert>
            <v-alert
              v-if="importResult.skipped.length"
              type="warning"
              text
              dense
            >
              ຂ້າມ {{ importResult.skipped.length }} ແຖວ:
              <div
                v-for="s in importResult.skipped"
                :key="s.row"
                class="text-caption"
              >
                ແຖວ {{ s.row }} ({{ s.code || '-' }}): {{ s.message }}
              </div>
            </v-alert>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="closeImport"
          >
            ປິດ
          </v-btn>
          <v-btn
            color="success"
            depressed
            :loading="importing"
            @click="importFile"
          >
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: 'HomeDeleteRequest',
  title() {
    return 'Vientiane Waste Co-Dev|Home Delete Request';
  },
  data() {
    return {
      filter: '',
      customers: [],
      loading: false,
      page: 1,
      pagination: {},
      selected: [],
      reason: '',
      reasonError: '',
      confirm: false,
      sending: false,
      mine: [],
      importDialog: false,
      file: null,
      fileError: '',
      importing: false,
      importResult: null,
      downloading: false,
      headers: [
        { text: 'ລູກຄ້າ', value: 'name' },
        { text: 'ທີ່ຢູ່', value: 'address' },
        { text: 'ແພັກເກດ', value: 'package' },
        { text: 'ສະຖານະ', value: 'status' },
      ],
      mineHeaders: [
        { text: 'ເລກຄຳຂໍ', value: 'id' },
        { text: 'ລູກຄ້າ', value: 'customer_name', sortable: false },
        { text: 'ເຫດຜົນ', value: 'reason', sortable: false },
        { text: 'ວັນທີສົ່ງ', value: 'created_at' },
        { text: 'ສະຖານະ', value: 'status' },
      ],
    };
  },
  created() {
    this.load(1);
    this.loadMine();
  },
  methods: {
    rowClass(item) {
      return item.selectable ? '' : 'grey lighten-4';
    },
    unselect(c) {
      this.selected = this.selected.filter((s) => s.id !== c.id);
    },
    clearFilter() {
      this.filter = '';
      this.load(1);
    },
    load(page) {
      this.page = page || 1;
      this.loading = true;
      this.$axios
        .get('home-delete-request/customers', { params: { page: this.page, per_page: 50, filter: this.filter || undefined } })
        .then((res) => {
          // selectable-key: blocked customers can't be ticked (select-all too).
          this.customers = (res.data.data.data || []).map((c) => ({ ...c, selectable: !c.blocked_reason }));
          this.pagination = res.data.data.pagination || {};
        })
        .catch((error) => this.toast('error', this.message(error)))
        .finally(() => {
          this.loading = false;
        });
    },
    loadMine() {
      this.$axios
        .get('home-delete-request', { params: { per_page: 100 } })
        .then((res) => {
          this.mine = res.data.data.data || [];
        })
        .catch(() => {});
    },
    openConfirm() {
      if (!this.reason || !this.reason.trim()) {
        this.reasonError = 'ກະລຸນາໃສ່ເຫດຜົນ';
        return;
      }
      this.confirm = true;
    },
    send() {
      this.sending = true;
      this.$axios
        .post('home-delete-request', { customer_ids: this.selected.map((c) => c.id), reason: this.reason.trim() })
        .then(() => {
          this.toast('success', 'ສົ່ງຄຳຂໍລຶບແລ້ວ, ລໍຖ້າອະນຸມັດຂັ້ນທີ 1');
          this.selected = [];
          this.reason = '';
          this.load(this.page);
          this.loadMine();
        })
        .catch((error) => this.toast('error', this.message(error)))
        .finally(() => {
          this.sending = false;
          this.confirm = false;
        });
    },
    downloadTemplate() {
      this.downloading = true;
      this.$axios
        .get('home-delete-request/template', { responseType: 'blob' })
        .then((res) => this.saveBlob(res.data, 'ແບບຟອມ_ລຶບລູກຄ້າສາຍບ້ານ.xlsx'))
        .catch(() => this.toast('error', 'ດາວໂຫລດບໍ່ສຳເລັດ'))
        .finally(() => {
          this.downloading = false;
        });
    },
    importFile() {
      if (!this.file) {
        this.fileError = 'ກະລຸນາເລືອກໄຟລ໌';
        return;
      }
      const fd = new FormData();
      fd.append('file', this.file);
      this.importing = true;
      this.importResult = null;
      this.$axios
        .post('home-delete-request/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then((res) => {
          this.importResult = res.data.data;
          this.file = null;
          this.load(1);
          this.loadMine();
        })
        .catch((error) => {
          const errors = (error.response && error.response.data && error.response.data.errors) || {};
          this.fileError = (errors.file && errors.file[0]) || this.message(error);
        })
        .finally(() => {
          this.importing = false;
        });
    },
    closeImport() {
      this.importDialog = false;
      this.importResult = null;
      this.file = null;
      this.fileError = '';
    },
    saveBlob(data, name) {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    message(error) {
      return (error.response && error.response.data && error.response.data.message) || 'Something went wrong';
    },
    toast(color, msg) {
      this.$store.commit('Toast_State', { value: true, color, msg });
    },
  },
};
</script>
